import Modal from '../Modal';
import { useOnboardingQuery } from '../../lib/Queries';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import useBusinessNavigate from '../../lib/useBusinessNavigate.js';
import { ArrowUpRightIcon, CheckIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useCompleteStep } from '../../lib/Onboarding.js';
import { isMobile } from '~/lib/Utils';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import SparklesIcon from '~/components/icons/SparklesIcon.jsx';
import { useEffect, useState } from 'react';

const OnboardingStep = ({ step, onClose, stepNumber }) => {
    const doNav = useBusinessNavigate();
    const { complete, isCompleting } = useCompleteStep();

    function navigate(href) {
        doNav(href);
        onClose();
    }

    const isCompleted = !!step.completed;

    return (
        <div className="sm:flex items-center justify-between py-4 pr-4">
            <div
                className="flex items-center cursor-pointer"
                onClick={() => navigate(step.href)}
            >
                <div className="mr-2 w-6 flex justify-center">
                    {isCompleted && (
                        <CheckIcon className="bg-primary-100 rounded-full p-1 text-primary-700 w-6 h-6" />
                    )}
                    {!isCompleted && !stepNumber && (
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    )}
                    {!isCompleted && !!stepNumber && (
                        <div className="w-6 h-6 border border-primary-100 text-xs flex items-center justify-center text-primary-700 rounded-full ">
                            {stepNumber}
                        </div>
                    )}
                </div>
                <div className={`flex items-center ${isCompleted ? 'text-gray-500' : 'text-primary-700 hover:underline'}`}>
                    {step.title}
                    {!isCompleted && (
                        <ArrowUpRightIcon className="w-4 h-4 ml-2" />
                    )}
                </div>
            </div>
            {!isCompleted && (
                <div className="flex items-center justify-end">
                    <a
                        href="#"
                        onClick={e => {
                            e.preventDefault();
                            complete(step.key);
                        }}
                        className="no-underline hover:underline uppercase text-gray-500 text-sm"
                    >
                        {isCompleting ? 'Skipping...' : 'Skip'}
                    </a>
                    {/*<button*/}
                    {/*    type="button"*/}
                    {/*    className="btn-light-pill"*/}
                    {/*    onClick={() => {*/}
                    {/*        navigate(step.href);*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    {step.button}*/}
                    {/*</button>*/}
                </div>
            )}
        </div>
    );
};

const StepsPanel = ({ isOpen, onClose, openAiChat }) => {
    const { data, isLoading } = useOnboardingQuery(isMobile());
    const steps = data || [];
    const [isAdditionalFeaturesOpen, setIsAdditionalFeaturesOpen] = useState(false);

    // Split out steps into required and optional based on steps[].optional
    const [requiredSteps, optionalSteps] = steps.reduce(
        (acc, step) => {
            acc[step.optional ? 1 : 0].push(step);
            return acc;
        },
        [[], []],
    );

    // If all the required steps are completed, then always show additional features
    const allRequiredStepsCompleted = requiredSteps.filter(s => !s.completed).length === 0;
    useEffect(() => {
        setIsAdditionalFeaturesOpen(allRequiredStepsCompleted);
    }, [allRequiredStepsCompleted]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            backgroundClassName="!bg-opacity-0"
            panelContainerClassName="!items-start sm:mt-10"
            panelClassName="!bg-white !p-0"
        >
            <div>
                <h4 className="font-medium p-4">Let’s get you booking in minutes!</h4>
                {/*<p className="text-gray-500 text-sm px-4 -mt-2 mb-4">*/}
                {/*    Start small with one service, personalize your form, and see exactly what your clients will experience. You can always add payments, packages, or subscriptions later.*/}
                {/*</p>*/}
            </div>
            <div className="divide-y border-t">
                {requiredSteps.map((step, index) => (
                    <div key={step.key} className="flex items-start pl-4">
                        <div className="flex-1">
                            <OnboardingStep
                                step={step}
                                onClose={onClose}
                                stepNumber={index + 1}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {optionalSteps.length > 0 && (
                <div className="mt-4">
                    <button
                        onClick={() => setIsAdditionalFeaturesOpen(!isAdditionalFeaturesOpen)}
                        className="flex items-center w-full px-4 py-2 text-left font-medium"
                    >
                        <span className="mr-2">Additional Features</span>
                        {isAdditionalFeaturesOpen ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                        )}
                    </button>
                    {isAdditionalFeaturesOpen && (
                        <div className="divide-y border-t">
                            {optionalSteps.map(step => (
                                <div key={step.key} className="flex items-start pl-4">
                                    <div className="flex-1">
                                        <OnboardingStep
                                            step={step}
                                            onClose={onClose}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <InfoMessageBlock
                className="mx-4 cursor-pointer mb-4 mt-4"
                onClick={() => {
                    onClose();
                    openAiChat();
                }}
            >
                Chat with the expert assistant to set up your
                account for you.
                <button className="btn-pill flex items-center mt-2">
                    <SparklesIcon className="w-4 h-4 mr-2" />
                    Try it now
                </button>
            </InfoMessageBlock>
        </Modal>
    );
};

export default StepsPanel;
